import * as Sentry from "@sentry/react";
import classNames from "classnames";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ALERTMESSAGE } from "../../../../enums";
import { getSupportedBanksDetails } from "../../../../setup/api/services/bank";
// types
import {
  ECurrency,
  IBankDetailsBody,
  IBankDetailsResponseData,
} from "../../../../setup/api/services/bank/model";
// components
import DropDown from "../../../common/dropDown";
import OutlinedButton from "../../../common/OutlinedButton";
import PrimaryButton from "../../../common/PrimaryButton";
import Spinner from "../../../common/Spinner";
import Alert, { AlertType } from "../../../common/WaffyAlert";
// constants
import TextInput, { InputType } from "../../../forms/textInput";
import { ALERT } from "../../address/address";
// styles
import styles from "./addPayment.module.scss";

interface Props {
  userBank?: IBankDetailsResponseData;
  updateBankInfo: (body: IBankDetailsBody) => void;
  alert?: ALERT;
  success: boolean;
}

const AddPayment = ({ userBank, updateBankInfo, alert, success }: Props) => {
  const [beneficiaryName, setBeneficiaryName] = useState<string>("");
  const [IBan, setIBan] = useState<string>("");
  const [bank, setBank] = useState<{ id: number; name: string }[] | undefined>(
    undefined
  );
  const [bankName, setBankName] = useState<
    { id: number; name: string } | undefined
  >(undefined);
  const [loading, setLoading] = useState(false);

  const [edit, setEdit] = useState<boolean>(false);

  useEffect(() => {
    userBank?.iban && setIBan(userBank?.iban);
    userBank?.bank &&
      setBankName({ id: userBank?.bank?.id, name: userBank?.bank?.arabicName });
    userBank?.beneficiaryName && setBeneficiaryName(userBank?.beneficiaryName);
  }, [userBank]);

  useEffect(() => {
    setEdit(!success);
  }, [success]);
  const getBanks = useCallback(async () => {
    try {
      const response = await getSupportedBanksDetails();
      const banks: { id: number; name: string }[] = [];
      response?.data?.map((bank) => {
        const bankObj = { id: bank?.id, name: bank?.arabicName };
        banks.push(bankObj);
      });
      setBank(banks);
    } catch (error) {
      Sentry.captureException(error);
      console.info("error bank", error);
    }
  }, []);
  const toggleEdit = () => {
    setEdit(!edit);
  };
  useEffect(() => {
    getBanks();
  }, [getBanks]);

  const handleOnSubmit = useCallback(() => {
    if (IBan && bankName?.id && beneficiaryName && edit) {
      const body = {
        iban: IBan,
        currency: ECurrency.SAR,
        beneficiaryName: beneficiaryName,
        bankId: bankName.id,
      };
      setLoading(true);
      updateBankInfo(body);
      setLoading(false);
      setEdit(false);
    } else {
      setEdit(false);
      setLoading(false);
    }
  }, [IBan, bankName?.id, beneficiaryName, edit, updateBankInfo]);
  const { t } = useTranslation();

  const Success = useCallback(() => {
    return (
      <>
        {alert !== ALERT.NONE && (
          <Alert
            type={
              alert === ALERT.SUCCESS ? AlertType.SUCCESS : AlertType.ERROR
            }>
            {alert === ALERT.SUCCESS
              ? t(ALERTMESSAGE.SUCCESS)
              : t(ALERTMESSAGE.ERROR)}
          </Alert>
        )}
      </>
    );
  }, [alert, t]);
  const renderOnSucces = () => {
    return (
      <>
        <div className={styles.text_wrapper_success}>
          <h2>{t("lbl_add_payment_means_of_receiving")}</h2>
          <p className={"p2"}>{t("lbl_add_payment_earnings")}</p>
        </div>
        <div className={classNames(styles.bank_wrapper, styles.row)}>
          <div className={"ml2"}>
            <div className={styles["bank_icon_wrapper"]}>
              <img
                src="/assets/images/svg/bank.svg"
                className={styles["dropdown_icon"]}
              />
            </div>
          </div>
          <div>
            <p dir="ltr" className={"subtitle1"}>
              •••• •••• •••• {IBan.substring(12, IBan.length)}
            </p>
            <p className={"p1"}>{bankName?.name}</p>
          </div>
        </div>
        <div className={classNames(styles["button_wrapper"])}>
          <OutlinedButton onClick={toggleEdit}>
            {" "}
            {t("lbl_edit_user_bank_account_btn")}
          </OutlinedButton>
        </div>
      </>
    );
  };
  return (
    <div className={styles["container"]}>
      {edit ? (
        <>
          <div className={styles.text_wrapper}>
            <h2>{t("lbl_add_payment_bank_details")}</h2>
            <p className={"p2"}>{t("lbl_add_payment_money_deposit")}</p>
          </div>
          <div>
            <div className={classNames("mb2")}>
              <TextInput
                label={t("lbl_add_bank_account_user_name")}
                defaultValue={beneficiaryName}
                required={true}
                handleChange={(e) => setBeneficiaryName(e?.target?.value)}
              />
            </div>
            <div className={classNames("mb2")}>
              <DropDown
                label={t("lbl_add_bank_account_bank_name")}
                shownValue={bankName}
                required={true}
                data={bank}
                handleChange={(value) => setBankName(value)}
              />
            </div>
            <div className={classNames("mb2")}>
              <TextInput
                label={t("lbl_add_bank_account_iban")}
                defaultValue={IBan}
                type={InputType.PASSWORD}
                required={true}
                handleChange={(e) => setIBan(e?.target?.value)}
              />
            </div>
          </div>
          <div className={classNames(styles["button_wrapper"], "mt2")}>
            <PrimaryButton
              disabled={
                !beneficiaryName || !IBan || !bank || !bankName || loading
              }
              id={`user_profile_add_account_save`}
              onClick={handleOnSubmit}>
              {loading ? <Spinner /> : t("lbl_add_and_save")}
            </PrimaryButton>
          </div>
        </>
      ) : (
        renderOnSucces()
      )}
      <Success />
    </div>
  );
};

export default AddPayment;
